import React from 'react';

export type AttributionMethod = 'STATIC_CODE' | 'SNEAKYLINK';

interface AttributionMethodOption {
  value: AttributionMethod;
  title: string;
  description: string;
  benefits: string[];
  icon: React.ReactNode;
}

interface AttributionMethodSelectorProps {
  selectedMethod: AttributionMethod;
  onMethodChange: (method: AttributionMethod) => void;
}

const methodOptions: AttributionMethodOption[] = [
  {
    value: 'STATIC_CODE',
    title: 'Static Discount Codes',
    description: 'Affiliates get a fixed discount code to share (e.g., JOHN10)',
    benefits: [
      'Simple and familiar',
      'Easy for affiliates to remember',
      'Codes can be shared publicly',
    ],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
      </svg>
    ),
  },
  {
    value: 'SNEAKYLINK',
    title: 'Sneakylinks (Unique Codes)',
    description: 'Generate a unique single-use code for each customer',
    benefits: [
      'Prevents code leaks',
      'Better attribution tracking',
      'Single-use codes expire automatically',
    ],
    icon: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
      </svg>
    ),
  },
];

export const AttributionMethodSelector: React.FC<AttributionMethodSelectorProps> = ({
  selectedMethod,
  onMethodChange,
}) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-muted-foreground mb-2">
          Attribution Method <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {methodOptions.map((option) => (
            <div
              key={option.value}
              onClick={() => onMethodChange(option.value)}
              className={`relative cursor-pointer rounded-xl border-2 p-6 transition-all duration-200 ${
                selectedMethod === option.value
                  ? 'border-brand-500 bg-brand-50 shadow-md'
                  : 'border-border hover:border-brand-300 hover:bg-accent'
              }`}
            >
              {selectedMethod === option.value && (
                <div className="absolute top-4 right-4">
                  <div className="bg-brand-500 rounded-full p-1">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              )}
              
              <div className="flex items-start gap-3 mb-3">
                <div className={`${selectedMethod === option.value ? 'text-brand-600' : 'text-muted-foreground'}`}>
                  {option.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-foreground mb-1">
                    {option.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    {option.description}
                  </p>
                </div>
              </div>
              
              <ul className="space-y-2">
                {option.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

